import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import { isTradeManagementReportType } from "../../../utils/reportTypeRules";
import { parentIdsFor } from "./workspaceReparentUtils";

export const MIN_COMBINED_POLICY_SOURCES = 2;

export type WorkspacePolicyLineage = {
  policyNode: WorkspaceNode;
  simulationNode: WorkspaceNode | null;
  reportNode: WorkspaceNode | null;
  strategyNode: WorkspaceNode | null;
};

/**
 * Walk policy → (derived policy)* → simulation → report → strategy for canvas lineage.
 * Federated Strategy graphs insert account between strategy and report
 * (`strategy-root → fed-account → report → …`).
 * Edit & run forks hang off a parent policy (`policy → derived policy → sim`), so we
 * must walk through policy parents before looking for a simulation.
 */
export function resolveWorkspacePolicyLineage(
  policyNode: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspacePolicyLineage {
  const byId = new Map(nodes.map((n) => [n.id, n]));

  let simulationNode: WorkspaceNode | null = null;
  {
    let cursor: WorkspaceNode | null = policyNode;
    const seen = new Set<string>();
    for (let hop = 0; hop < 12 && cursor; hop += 1) {
      if (seen.has(cursor.id)) break;
      seen.add(cursor.id);
      const parentId: string | null = parentIdsFor(edges, cursor.id)[0] ?? null;
      const parent: WorkspaceNode | null = parentId ? byId.get(parentId) ?? null : null;
      if (!parent) break;
      if (parent.type === "simulation" || parent.type === "combined_simulation") {
        simulationNode = parent;
        break;
      }
      if (parent.type === "policy" || parent.type === "combined_policy") {
        cursor = parent;
        continue;
      }
      break;
    }
  }

  let reportNode: WorkspaceNode | null = null;
  if (simulationNode) {
    let cursor: WorkspaceNode | null = simulationNode;
    const seen = new Set<string>();
    for (let hop = 0; hop < 8 && cursor; hop += 1) {
      if (seen.has(cursor.id)) break;
      seen.add(cursor.id);
      const parentId: string | null = parentIdsFor(edges, cursor.id)[0] ?? null;
      const parent: WorkspaceNode | null = parentId ? byId.get(parentId) ?? null : null;
      if (!parent) break;
      if (parent.type === "report") {
        reportNode = parent;
        break;
      }
      // Date-window reruns hang under another simulation; derived sims may sit under a policy.
      if (
        parent.type === "simulation" ||
        parent.type === "combined_simulation" ||
        parent.type === "policy" ||
        parent.type === "combined_policy"
      ) {
        cursor = parent;
        continue;
      }
      break;
    }
  }

  let strategyNode: WorkspaceNode | null = null;
  if (reportNode) {
    let cursorId: string | null = parentIdsFor(edges, reportNode.id)[0] ?? null;
    // Walk past account (federated) or land on strategy (account canvas).
    for (let hop = 0; hop < 4 && cursorId; hop += 1) {
      const ancestor: WorkspaceNode | null = byId.get(cursorId) ?? null;
      if (!ancestor) break;
      if (ancestor.type === "strategy") {
        strategyNode = ancestor;
        break;
      }
      if (ancestor.type === "account") {
        cursorId = parentIdsFor(edges, ancestor.id)[0] ?? null;
        continue;
      }
      break;
    }
  }

  // Fallback for odd graphs: BFS any strategy ancestor from the policy.
  if (!strategyNode) {
    const parentsByTarget = new Map<string, string[]>();
    for (const edge of edges) {
      const list = parentsByTarget.get(edge.targetNodeId) ?? [];
      list.push(edge.sourceNodeId);
      parentsByTarget.set(edge.targetNodeId, list);
    }
    const queue = [policyNode.id];
    const seen = new Set<string>();
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const node = byId.get(id);
      if (node?.type === "strategy" && node.entityId.trim()) {
        strategyNode = node;
        break;
      }
      for (const parentId of parentsByTarget.get(id) ?? []) {
        queue.push(parentId);
      }
    }
  }

  return { policyNode, simulationNode, reportNode, strategyNode };
}

export function strategyIdForPolicyNode(
  policyNode: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): string | null {
  return resolveWorkspacePolicyLineage(policyNode, nodes, edges).strategyNode?.entityId?.trim() || null;
}

function isCombineSourceParent(node: WorkspaceNode | undefined): node is WorkspaceNode {
  return Boolean(
    node &&
      !node.isDisabled &&
      (node.type === "policy" || node.type === "combined_policy")
  );
}

/** Direct source parents of a combined_policy (edge order): policies and/or combined_policies. */
export function listCombinedPolicySourceNodes(
  combinedPolicyNode: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode[] {
  if (combinedPolicyNode.type !== "combined_policy") return [];
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const fromEdges = parentIdsFor(edges, combinedPolicyNode.id)
    .map((id) => byId.get(id))
    .filter(isCombineSourceParent);
  if (fromEdges.length > 0) return fromEdges;

  // Fallback when parents are not on this canvas (meta provenance only).
  const versionIds = (combinedPolicyNode.meta?.sourcePolicyVersionIds ?? [])
    .map((id) => String(id).trim())
    .filter(Boolean);
  if (versionIds.length === 0) return [];
  const out: WorkspaceNode[] = [];
  const seen = new Set<string>();
  for (const versionId of versionIds) {
    const match = nodes.find(
      (n) => isCombineSourceParent(n) && n.entityId === versionId
    );
    if (!match || seen.has(match.id)) continue;
    seen.add(match.id);
    out.push(match);
  }
  return out;
}

/** Strategy id for a single policy or combined_policy (via its source policies). */
export function strategyIdForCombineCandidate(
  node: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): string | null {
  if (node.type === "policy") {
    return strategyIdForPolicyNode(node, nodes, edges);
  }
  if (node.type === "combined_policy") {
    for (const source of listCombinedPolicySourceNodes(node, nodes, edges)) {
      const sid = strategyIdForCombineCandidate(source, nodes, edges);
      if (sid) return sid;
    }
  }
  return null;
}

/**
 * Expand a combine selection of policies / combined_policies into ordered unique
 * singular policy nodes (deduped by entity id). Nested combined policies are
 * walked recursively so the API still receives leaf policy version ids.
 * Canvas parent edges should use the selection itself, not this expansion.
 */
export function expandCombineSelectionToSourcePolicies(
  selectedNodesInOrder: WorkspaceNode[],
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode[] {
  const out: WorkspaceNode[] = [];
  const seenEntityIds = new Set<string>();
  const visiting = new Set<string>();

  const pushPolicy = (policy: WorkspaceNode) => {
    if (policy.type !== "policy" || policy.isDisabled) return;
    const entityId = policy.entityId?.trim();
    if (!entityId || seenEntityIds.has(entityId)) return;
    seenEntityIds.add(entityId);
    out.push(policy);
  };

  const expandNode = (node: WorkspaceNode) => {
    if (node.type === "policy") {
      pushPolicy(node);
      return;
    }
    if (node.type !== "combined_policy" || visiting.has(node.id)) return;
    visiting.add(node.id);
    for (const source of listCombinedPolicySourceNodes(node, nodes, edges)) {
      expandNode(source);
    }
    visiting.delete(node.id);
  };

  for (const node of selectedNodesInOrder) {
    expandNode(node);
  }
  return out;
}

/**
 * Policies and combined policies that share the same strategy as `anchor`
 * (or any policy family node if the anchor has no resolvable strategy).
 */
export function listCombinablePolicyNodes(
  anchor: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode[] {
  if (anchor.type !== "policy" && anchor.type !== "combined_policy") return [];
  const strategyId = strategyIdForCombineCandidate(anchor, nodes, edges);

  return nodes.filter((node) => {
    if (node.isDisabled) return false;
    if (node.type !== "policy" && node.type !== "combined_policy") return false;
    if (!strategyId) return true;
    const nodeStrategy = strategyIdForCombineCandidate(node, nodes, edges);
    // Include peers that share the strategy, and peers whose strategy is still
    // unresolved (e.g. mid-hydrate) so combine selection is not prematurely dimmed.
    return !nodeStrategy || nodeStrategy === strategyId;
  });
}

/**
 * First-selected policy / combined policy determines the adopted report / date window.
 */
export function resolveAdoptedReportFromPolicySelection(
  selectedPolicyNodesInOrder: WorkspaceNode[],
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): {
  adoptedReportId: string | null;
  adoptedReportTitle: string | null;
  firstPolicyNodeId: string | null;
} {
  const first = selectedPolicyNodesInOrder[0] ?? null;
  if (!first) {
    return { adoptedReportId: null, adoptedReportTitle: null, firstPolicyNodeId: null };
  }
  if (first.type === "combined_policy") {
    const adoptedReportId = first.meta?.adoptedReportId?.trim() || null;
    const reportNode = adoptedReportId
      ? nodes.find((n) => n.type === "report" && n.entityId === adoptedReportId) ?? null
      : null;
    return {
      adoptedReportId,
      adoptedReportTitle: reportNode?.meta?.title?.trim() || null,
      firstPolicyNodeId: first.id,
    };
  }
  const lineage = resolveWorkspacePolicyLineage(first, nodes, edges);
  return {
    adoptedReportId: lineage.reportNode?.entityId?.trim() || null,
    adoptedReportTitle: lineage.reportNode?.meta?.title?.trim() || null,
    firstPolicyNodeId: first.id,
  };
}

/** Parent combined_policy for a combined_simulation, if present. */
export function findParentCombinedPolicyNode(
  combinedSimulationNode: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): WorkspaceNode | null {
  if (combinedSimulationNode.type !== "combined_simulation") return null;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const parentId of parentIdsFor(edges, combinedSimulationNode.id)) {
    const parent = byId.get(parentId);
    if (parent?.type === "combined_policy") return parent;
  }
  return null;
}

/** Build cross-workspace combine `sources[]` from federated (or account) policy nodes. */
export function buildCombinePolicySources(
  selectedPolicyNodesInOrder: WorkspaceNode[]
): Array<{
  policyVersionId: string;
  workspaceId: string | null;
  policyNodeId: string | null;
}> {
  return selectedPolicyNodesInOrder.map((n) => ({
    policyVersionId: n.entityId,
    workspaceId: n.sourceWorkspaceId?.trim() || null,
    policyNodeId: n.sourceNodeId?.trim() || null,
  }));
}

/**
 * Home canvas for registering a combined_policy: first selection’s provenance,
 * else the active account workspace id when not in strategy view.
 */
export function resolveCombineHomeWorkspaceId(
  selectedPolicyNodesInOrder: WorkspaceNode[],
  options?: { viewMode?: "account" | "strategy"; fallbackWorkspaceId?: string | null }
): string | null {
  const fromProvenance = selectedPolicyNodesInOrder[0]?.sourceWorkspaceId?.trim() || null;
  if (fromProvenance) return fromProvenance;
  if (options?.viewMode === "strategy") return null;
  const fallback = options?.fallbackWorkspaceId?.trim() || null;
  return fallback;
}

/**
 * Lineage heuristic for combine stack UI when policy trees are not loaded.
 * Exit Placement reports → management; other reports → entry; missing → unknown.
 */
export function classifyCombinePolicySourceKind(
  policyNode: WorkspaceNode,
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): "management" | "entry" | "unknown" {
  if (policyNode.type !== "policy") return "unknown";
  const { reportNode } = resolveWorkspacePolicyLineage(policyNode, nodes, edges);
  const reportType = reportNode?.meta?.reportType;
  if (!reportType?.trim()) return "unknown";
  if (isTradeManagementReportType(reportType)) return "management";
  return "entry";
}
